import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../assets/admin_logo.png";

const PRIMARY_COLOR = [206, 37, 122]; // Mogzit pink
const HEADER_COLOR = [30, 41, 59]; // Blue-black (slate-800)
const SECONDARY_COLOR = [100, 100, 100]; // Gray
const ACCENT_COLOR = [245, 245, 250]; // Light background

const READABLE_WORD_REPLACEMENTS = {
  prep: "Preparation",
  childrens: "Children's",
};

const formatReadableText = (value) => {
  const normalized = String(value ?? "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!normalized) return "";

  const words = normalized.split(" ");
  const readableWords = [];

  for (let index = 0; index < words.length; index += 1) {
    const word = words[index];
    const lowerWord = word.toLowerCase();
    const nextWord = words[index + 1]?.toLowerCase();

    if (lowerWord === "infant" && nextWord === "toddler") {
      readableWords.push("Infant & Toddler");
      index += 1;
      continue;
    }

    readableWords.push(
      READABLE_WORD_REPLACEMENTS[lowerWord] ||
        `${word.charAt(0).toUpperCase()}${word.slice(1)}`,
    );
  }

  return readableWords.join(" ");
};

const formatDynamicValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .map(formatDynamicValue)
      .filter(Boolean)
      .join(", ");
  }

  if (value && typeof value === "object") {
    return Object.entries(value)
      .filter(([, nestedValue]) => nestedValue !== null && nestedValue !== "")
      .map(
        ([key, nestedValue]) =>
          `${formatReadableText(key)}: ${formatDynamicValue(nestedValue)}`,
      )
      .filter((entry) => !entry.endsWith(": "))
      .join("; ");
  }

  if (typeof value === "boolean") return value ? "Yes" : "No";
  return formatReadableText(value);
};

const formatMonthlyFee = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "Negotiable";
  return `${amount.toLocaleString("en-US")} ETB`;
};

export const generateCaregiverCVPDF = async (c) => {
  const doc = new jsPDF("p", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;

  /* ======================
     HEADER WITH BRAND
  ====================== */
  // Brand header background - Blue-black
  doc.setFillColor(...HEADER_COLOR);
  doc.rect(0, 0, pageWidth, 35, "F");

  // Logo
  try {
    doc.addImage(logo, "PNG", margin, 8, 20, 20);
  } catch {
    console.warn("Logo not loaded");
  }

  // Company name
  doc.setFontSize(18);
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.text("Mogzit In Home Care", margin + 25, 16);

  // Document title
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.text("Professional Caregiver Profile", margin + 25, 23);

  /* ======================
     PROFILE SECTION
  ====================== */
  let currentY = 45;

  // Profile photo - Rectangular with blue-black border
  const photoX = margin;
  const photoY = currentY;
  const photoSize = 35;

  if (c.image) {
    try {
      const img = await loadImageAsBase64(c.image);

      // Photo
      doc.addImage(img, "JPEG", photoX, photoY, photoSize, photoSize);

      // Blue-black rectangular border
      doc.setDrawColor(...HEADER_COLOR);
      doc.setLineWidth(2);
      doc.rect(photoX - 1, photoY - 1, photoSize + 2, photoSize + 2);
    } catch {
      // Placeholder if image fails - rectangular
      doc.setFillColor(220, 220, 220);
      doc.rect(photoX, photoY, photoSize, photoSize, "F");

      // Blue-black border
      doc.setDrawColor(...HEADER_COLOR);
      doc.setLineWidth(2);
      doc.rect(photoX - 1, photoY - 1, photoSize + 2, photoSize + 2);

      // Initial letter
      doc.setFontSize(24);
      doc.setTextColor(150, 150, 150);
      doc.text(
        (c.name || "?").charAt(0).toUpperCase(),
        photoX + photoSize / 2,
        photoY + photoSize / 2 + 5,
        { align: "center" },
      );
    }
  }

  // Name and title
  const nameX = photoX + photoSize + 10;
  doc.setFontSize(16);
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.text(c.name || "Caregiver Name", nameX, currentY + 8);

  doc.setFontSize(11);
  doc.setTextColor(...SECONDARY_COLOR);
  doc.setFont("helvetica", "normal");
  doc.text(c.category || "Caregiver", nameX, currentY + 15);

  // Rating display - Fixed
  if (c.rating) {
    const rating = parseFloat(c.rating);
    doc.setFontSize(10);
    doc.setTextColor(...PRIMARY_COLOR);
    doc.setFont("helvetica", "bold");
    doc.text(`Rating: ${rating.toFixed(1)} / 5.0`, nameX, currentY + 21);
  }

  currentY = photoY + photoSize + 15;

  /* ======================
     CONTACT INFORMATION - Fixed without emojis
  ====================== */
  doc.setFillColor(...ACCENT_COLOR);
  doc.rect(margin, currentY, pageWidth - 2 * margin, 30, "F");

  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.text("Contact Information", margin + 3, currentY + 7);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...SECONDARY_COLOR);

  const contactY = currentY + 13;
  const col1X = margin + 3;
  const col2X = pageWidth / 2 + 5;

  // Column 1 - No emojis, just text labels
  doc.text(`Email: ${c.email || "N/A"}`, col1X, contactY);
  doc.text(
    `Location: ${c.subcity || "N/A"}, Addis Ababa`,
    col1X,
    contactY + 6,
  );

  // Column 2
  doc.text(`Age: ${c.age || "N/A"}`, col2X, contactY);
  doc.text(`Gender: ${formatReadableText(c.gender) || "N/A"}`, col2X, contactY + 6);
  doc.text(`Nationality: ${c.nationality || "N/A"}`, col2X, contactY + 12);

  currentY += 35;

  /* ======================
     PROFESSIONAL SUMMARY
  ====================== */
  if (c.about) {
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.text("Professional Summary", margin, currentY);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...SECONDARY_COLOR);
    const aboutLines = doc.splitTextToSize(c.about, pageWidth - 2 * margin);
    doc.text(aboutLines, margin, currentY + 6);

    currentY += 6 + aboutLines.length * 5 + 8;
  }

  /* ======================
     KEY QUALIFICATIONS
  ====================== */
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.text("Key Qualifications", margin, currentY);

  currentY += 2;

  autoTable(doc, {
    startY: currentY,
    head: [["Qualification", "Details"]],
    body: [
      ["Education", c.education || "Not specified"],
      ["Experience", c.experience ? `${c.experience} Years` : "Not specified"],
      [
        "Languages",
        formatDynamicValue(c.languages) || "Not specified",
      ],
      ["Work Preference", c.date_time_preference_to_work || "Flexible"],
      ["Monthly Fee", formatMonthlyFee(c.fees)],
    ],
    styles: {
      fontSize: 10,
      cellPadding: 4,
      lineColor: [220, 220, 220],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: HEADER_COLOR,
      textColor: 255,
      fontStyle: "bold",
      halign: "left",
    },
    columnStyles: {
      0: { cellWidth: 50, fontStyle: "bold", textColor: [60, 60, 60] },
      1: { cellWidth: "auto" },
    },
    alternateRowStyles: {
      fillColor: [252, 252, 252],
    },
    margin: { left: margin, right: margin },
  });

  currentY = doc.lastAutoTable.finalY + 10;

  /* ======================
     SPECIALIZED SKILLS - Dynamic from fieldValues
  ====================== */
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.text("Specialized Skills & Experience", margin, currentY);

  currentY += 2;

  // Build dynamic table from all field values
  const skillsData = [];

  // Extract all field values dynamically
  Object.keys(c).forEach((key) => {
    // Skip standard fields that are already shown
    const skipFields = [
      "_id",
      "name",
      "email",
      "phoneNumber",
      "image",
      "gender",
      "age",
      "nationality",
      "subcity",
      "category",
      "experience",
      "education",
      "languages",
      "date_time_preference_to_work",
      "fees",
      "rating",
      "about",
      "_fieldLabels",
    ];

    if (skipFields.includes(key)) return;

    const value = c[key];

    // Skip null, undefined, or empty values
    if (value === null || value === undefined || value === "") return;

    // Format the key as a readable label
    const label = formatReadableText(c._fieldLabels?.[key] || key);
    const formattedValue = formatDynamicValue(value);

    if (formattedValue && formattedValue.toString().trim()) {
      skillsData.push([label, formattedValue.toString()]);
    }
  });

  if (skillsData.length > 0) {
    autoTable(doc, {
      startY: currentY,
      head: [["Skill / Qualification", "Details"]],
      body: skillsData,
      styles: {
        fontSize: 10,
        cellPadding: 3,
        lineColor: [220, 220, 220],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: PRIMARY_COLOR,
        textColor: 255,
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 60, fontStyle: "bold", textColor: [60, 60, 60] },
        1: { cellWidth: "auto" },
      },
      alternateRowStyles: {
        fillColor: [252, 252, 252],
      },
      margin: { left: margin, right: margin },
    });
    currentY = doc.lastAutoTable.finalY + 8;
  }

  /* ======================
     FOOTER
  ====================== */
  const footerY = pageHeight - 15;

  // Footer line
  doc.setDrawColor(...PRIMARY_COLOR);
  doc.setLineWidth(0.5);
  doc.line(margin, footerY, pageWidth - margin, footerY);

  // Footer text
  doc.setFontSize(8);
  doc.setTextColor(...SECONDARY_COLOR);
  doc.setFont("helvetica", "italic");
  doc.text(
    "This CV is generated by Mogzit In Home Care Platform",
    pageWidth / 2,
    footerY + 5,
    { align: "center" },
  );

  doc.text(
    `Generated on: ${new Date().getFullYear()}`,
    pageWidth / 2,
    footerY + 9,
    { align: "center" },
  );

  // Save
  const fileName = `${(c.name || "Caregiver").replaceAll(" ", "_")}_CV_Mogzit.pdf`;
  doc.save(fileName);
};

/* ======================
   IMAGE LOADER (BASE64)
====================== */
const loadImageAsBase64 = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/jpeg"));
    };
    img.onerror = reject;
    img.src = url;
  });
